import { useEffect, useMemo, useState } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';

import {
  Users,
  CalendarDays,
  CreditCard,
  ClipboardCheck,
  Megaphone,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Loader2,
  UserRound,
  ChevronDown
} from 'lucide-react';

import {
  Card,
  CardContent
} from '@/components/ui/card';

import {
  Button
} from '@/components/ui/button';

import {
  Badge
} from '@/components/ui/badge';


const ABAS = [
  {
    id: 'resumo',
    label: 'Resumo',
    icon: UserRound
  },
  {
    id: 'treinos',
    label: 'Treinos',
    icon: CalendarDays
  },
  {
    id: 'presencas',
    label: 'Presenças',
    icon: ClipboardCheck
  },
  {
    id: 'mensalidades',
    label: 'Mensalidades',
    icon: CreditCard
  },
  {
    id: 'avisos',
    label: 'Avisos',
    icon: Megaphone
  }
];


function formatarData(data) {
  if (!data) return '-';

  try {
    const parte = String(data).slice(0, 10);
    const [ano, mes, dia] = parte.split('-');

    if (!ano || !mes || !dia) return data;

    return `${dia}/${mes}/${ano}`;
  } catch {
    return data;
  }
}


function formatarCompetencia(valor) {
  if (!valor) return '-';

  const [ano, mes] = String(valor).split('-');

  if (!ano || !mes) return valor;

  return `${mes}/${ano}`;
}


function formatarMoeda(valor) {
  return Number(valor || 0).toLocaleString(
    'pt-BR',
    {
      style: 'currency',
      currency: 'BRL'
    }
  );
}


function labelStatusMensalidade(status) {
  const mapa = {
    pago: 'Pago',
    atrasado: 'Atrasado',
    aguardando_pagamento: 'Aguardando pagamento',
    comprovante_enviado: 'Comprovante enviado',
    cancelado: 'Cancelado'
  };

  return mapa[status] || status || '-';
}


function badgeStatusMensalidade(status) {
  if (status === 'pago') {
    return (
      <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/20">
        Pago
      </Badge>
    );
  }

  if (status === 'atrasado') {
    return (
      <Badge className="bg-red-500/15 text-red-400 border-red-500/20">
        Atrasado
      </Badge>
    );
  }

  if (status === 'comprovante_enviado') {
    return (
      <Badge className="bg-blue-500/15 text-blue-400 border-blue-500/20">
        Comprovante enviado
      </Badge>
    );
  }

  return (
    <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/20">
      {labelStatusMensalidade(status)}
    </Badge>
  );
}


function EmptyState({ texto }) {
  return (
    <div className="py-10 text-center text-sm text-zinc-500">
      {texto}
    </div>
  );
}


export default function ResponsavelPortalPage() {
  const [filhos, setFilhos] = useState([]);
  const [alunoId, setAlunoId] = useState('');
  const [aba, setAba] = useState('resumo');

  const [dashboard, setDashboard] = useState(null);
  const [treinos, setTreinos] = useState([]);
  const [mensalidades, setMensalidades] = useState([]);
  const [presencas, setPresencas] = useState([]);
  const [avisos, setAvisos] = useState([]);

  const [loadingFilhos, setLoadingFilhos] = useState(true);
  const [loadingDados, setLoadingDados] = useState(false);


  const filhoSelecionado = useMemo(
    () => filhos.find(
      filho => filho.id === alunoId
    ),
    [filhos, alunoId]
  );


  async function carregarFilhos() {
    setLoadingFilhos(true);

    try {
      const { data } = await api.get(
        '/responsavel/meus-filhos'
      );

      const lista = Array.isArray(data)
        ? data
        : [];

      setFilhos(lista);

      if (!lista.length) {
        setAlunoId('');
        return;
      }

      const salvo = localStorage.getItem(
        'responsavel_aluno_selecionado'
      );

      const existeSalvo = lista.some(
        filho => filho.id === salvo
      );

      const escolhido = existeSalvo
        ? salvo
        : lista[0].id;

      setAlunoId(escolhido);
    } catch (error) {
      toast.error(
        formatApiError(
          error,
          'Não foi possível carregar os filhos vinculados.'
        )
      );
    } finally {
      setLoadingFilhos(false);
    }
  }


  async function carregarDados(id) {
    if (!id) return;

    setLoadingDados(true);

    try {
      const [
        dashboardResp,
        treinosResp,
        mensalidadesResp,
        presencasResp,
        avisosResp
      ] = await Promise.all([
        api.get(
          `/responsavel/portal/${id}/dashboard`
        ),
        api.get(
          `/responsavel/portal/${id}/treinos`
        ),
        api.get(
          `/responsavel/portal/${id}/mensalidades`
        ),
        api.get(
          `/responsavel/portal/${id}/presencas`
        ),
        api.get(
          `/responsavel/portal/${id}/avisos`
        )
      ]);

      setDashboard(
        dashboardResp.data || null
      );

      setTreinos(
        Array.isArray(treinosResp.data)
          ? treinosResp.data
          : []
      );

      setMensalidades(
        Array.isArray(mensalidadesResp.data)
          ? mensalidadesResp.data
          : []
      );

      setPresencas(
        Array.isArray(presencasResp.data)
          ? presencasResp.data
          : []
      );

      setAvisos(
        Array.isArray(avisosResp.data)
          ? avisosResp.data
          : []
      );
    } catch (error) {
      toast.error(
        formatApiError(
          error,
          'Não foi possível carregar os dados deste aluno.'
        )
      );
    } finally {
      setLoadingDados(false);
    }
  }


  useEffect(() => {
    carregarFilhos();
  }, []);


  useEffect(() => {
    if (!alunoId) return;

    localStorage.setItem(
      'responsavel_aluno_selecionado',
      alunoId
    );

    setAba('resumo');
    carregarDados(alunoId);
  }, [alunoId]);


  if (loadingFilhos) {
    return (
      <div className="flex items-center justify-center min-h-[50vh] text-zinc-400">
        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
        Carregando família...
      </div>
    );
  }


  if (!filhos.length) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-black text-white">
            Portal da Família
          </h1>

          <p className="text-sm text-zinc-500 mt-1">
            Acompanhe a rotina esportiva dos seus filhos.
          </p>
        </div>

        <Card className="bg-[#111113] border-[#27272A]">
          <CardContent className="py-12 text-center">
            <Users className="w-10 h-10 mx-auto text-zinc-600 mb-4" />

            <h2 className="text-lg font-semibold text-white">
              Nenhum aluno vinculado
            </h2>

            <p className="text-sm text-zinc-500 mt-2 max-w-md mx-auto">
              Seu acesso ainda não possui alunos vinculados.
              Entre em contato com a gestão da escola.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }


  const frequencia = dashboard?.frequencia || {};
  const proximoTreino = dashboard?.proximo_treino;
  const mensalidadeAtual = dashboard?.mensalidade;


  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <p className="text-xs font-bold tracking-[0.2em] text-zinc-500 uppercase">
            Família
          </p>

          <h1 className="text-2xl md:text-3xl font-black text-white mt-1">
            Meus Filhos
          </h1>

          <p className="text-sm text-zinc-500 mt-1">
            Acompanhe treinos, presença, mensalidades e avisos.
          </p>
        </div>

        <Button
          variant="outline"
          onClick={() => {
            carregarFilhos();

            if (alunoId) {
              carregarDados(alunoId);
            }
          }}
          disabled={loadingDados}
          className="border-[#27272A] bg-[#111113] text-zinc-300 hover:bg-[#1C1C1E]"
        >
          {loadingDados ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-2" />
          )}

          Atualizar
        </Button>
      </div>


      {filhos.length > 1 ? (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {filhos.map(filho => {
            const ativo = filho.id === alunoId;

            return (
              <button
                key={filho.id}
                type="button"
                onClick={() => setAlunoId(filho.id)}
                className={[
                  'text-left rounded-2xl border p-4 transition-all',
                  ativo
                    ? 'border-[var(--cor-primaria,#007AFF)] bg-[color-mix(in_srgb,var(--cor-primaria,#007AFF)_12%,transparent)]'
                    : 'border-[#27272A] bg-[#111113] hover:bg-[#171719]'
                ].join(' ')}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                    style={{
                      backgroundColor:
                        'color-mix(in srgb, var(--cor-primaria, #007AFF) 18%, transparent)'
                    }}
                  >
                    <UserRound
                      className="w-5 h-5"
                      style={{
                        color:
                          'var(--cor-primaria, #007AFF)'
                      }}
                    />
                  </div>

                  <div className="min-w-0">
                    <p className="font-semibold text-white truncate">
                      {filho.name}
                    </p>

                    <p className="text-xs text-zinc-500 truncate">
                      {filho.parentesco || 'Aluno vinculado'}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <Card className="bg-[#111113] border-[#27272A]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center"
                style={{
                  backgroundColor:
                    'color-mix(in srgb, var(--cor-primaria, #007AFF) 18%, transparent)'
                }}
              >
                <UserRound
                  className="w-5 h-5"
                  style={{
                    color:
                      'var(--cor-primaria, #007AFF)'
                  }}
                />
              </div>

              <div>
                <p className="font-semibold text-white">
                  {filhoSelecionado?.name}
                </p>

                <p className="text-xs text-zinc-500">
                  {filhoSelecionado?.parentesco || 'Aluno vinculado'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}


      <div className="overflow-x-auto">
        <div className="inline-flex min-w-max gap-2 p-1.5 rounded-xl border border-[#27272A] bg-[#111113]">
          {ABAS.map(item => {
            const Icon = item.icon;
            const ativa = aba === item.id;

            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setAba(item.id)}
                className={[
                  'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition',
                  ativa
                    ? 'text-white'
                    : 'text-zinc-500 hover:text-white hover:bg-[#1C1C1E]'
                ].join(' ')}
                style={
                  ativa
                    ? {
                        backgroundColor:
                          'color-mix(in srgb, var(--cor-primaria, #007AFF) 20%, transparent)'
                      }
                    : {}
                }
              >
                <Icon className="w-4 h-4" />
                {item.label}
              </button>
            );
          })}
        </div>
      </div>


      {loadingDados ? (
        <div className="flex items-center justify-center py-16 text-zinc-500">
          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          Carregando dados...
        </div>
      ) : (
        <>
          {aba === 'resumo' && (
            <div className="space-y-6">
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <Card className="bg-[#111113] border-[#27272A]">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <CalendarDays className="w-5 h-5 text-zinc-500" />
                      <Clock className="w-4 h-4 text-zinc-700" />
                    </div>

                    <p className="text-xs uppercase tracking-wider text-zinc-500 mt-5">
                      Próximo treino
                    </p>

                    {proximoTreino ? (
                      <>
                        <p className="text-lg font-bold text-white mt-1">
                          {formatarData(proximoTreino.data)}
                        </p>

                        <p className="text-sm text-zinc-400">
                          {proximoTreino.horario || '-'}
                          {proximoTreino.horario_fim
                            ? ` às ${proximoTreino.horario_fim}`
                            : ''}
                        </p>

                        <p className="text-xs text-zinc-600 mt-1 truncate">
                          {proximoTreino.turma_nome || proximoTreino.nome || ''}
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-zinc-500 mt-2">
                        Nenhum treino futuro.
                      </p>
                    )}
                  </CardContent>
                </Card>


                <Card
                  className={
                    mensalidadeAtual?.status === 'atrasado'
                      ? 'bg-red-500/[0.06] border-red-500/30'
                      : 'bg-[#111113] border-[#27272A]'
                  }
                >
                  <CardContent className="p-5">
                    <CreditCard
                      className={
                        mensalidadeAtual?.status === 'atrasado'
                          ? 'w-5 h-5 text-red-400'
                          : 'w-5 h-5 text-zinc-500'
                      }
                    />

                    <p className="text-xs uppercase tracking-wider text-zinc-500 mt-5">
                      Mensalidade
                    </p>

                    {mensalidadeAtual ? (
                      <>
                        <p className="text-lg font-bold text-white mt-1">
                          {formatarMoeda(mensalidadeAtual.valor)}
                        </p>

                        <div className="mt-2">
                          {badgeStatusMensalidade(
                            mensalidadeAtual.status
                          )}
                        </div>

                        {mensalidadeAtual.status === 'atrasado' && (
                          <div className="flex items-center gap-1.5 mt-3 text-xs font-semibold text-red-400">
                            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                            Pagamento em atraso
                          </div>
                        )}

                        <p
                          className={
                            mensalidadeAtual.status === 'atrasado'
                              ? 'text-xs text-red-300/70 mt-2'
                              : 'text-xs text-zinc-600 mt-2'
                          }
                        >
                          Vence em {formatarData(
                            mensalidadeAtual.data_vencimento
                          )}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-lg font-bold text-emerald-400 mt-1">
                          Em dia
                        </p>

                        <p className="text-xs text-zinc-600 mt-2">
                          Nenhuma mensalidade pendente.
                        </p>
                      </>
                    )}
                  </CardContent>
                </Card>


                <Card className="bg-[#111113] border-[#27272A]">
                  <CardContent className="p-5">
                    <ClipboardCheck className="w-5 h-5 text-zinc-500" />

                    <p className="text-xs uppercase tracking-wider text-zinc-500 mt-5">
                      Frequência no mês
                    </p>

                    <p className="text-2xl font-black text-white mt-1">
                      {frequencia.percentual || 0}%
                    </p>

                    <p className="text-xs text-zinc-600 mt-2">
                      {frequencia.presencas || 0} presença(s)
                      {' · '}
                      {frequencia.faltas || 0} falta(s)
                    </p>
                  </CardContent>
                </Card>


                <Card className="bg-[#111113] border-[#27272A]">
                  <CardContent className="p-5">
                    <Megaphone className="w-5 h-5 text-zinc-500" />

                    <p className="text-xs uppercase tracking-wider text-zinc-500 mt-5">
                      Avisos recentes
                    </p>

                    <p className="text-2xl font-black text-white mt-1">
                      {dashboard?.avisos?.length || 0}
                    </p>

                    <p className="text-xs text-zinc-600 mt-2">
                      Últimos comunicados exibidos.
                    </p>
                  </CardContent>
                </Card>
              </div>


              <Card className="bg-[#111113] border-[#27272A]">
                <CardContent className="p-5">
                  <h2 className="font-bold text-white">
                    Turmas
                  </h2>

                  <div className="flex flex-wrap gap-2 mt-3">
                    {dashboard?.aluno?.turmas?.length ? (
                      dashboard.aluno.turmas.map(turma => (
                        <Badge
                          key={turma.id}
                          variant="outline"
                          className="border-[#323238] text-zinc-300"
                        >
                          {turma.nome}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-sm text-zinc-500">
                        Nenhuma turma vinculada.
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>


              <Card className="bg-[#111113] border-[#27272A]">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between gap-3">
                    <h2 className="font-bold text-white">
                      Avisos recentes
                    </h2>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setAba('avisos')}
                      className="text-zinc-400"
                    >
                      Ver todos
                    </Button>
                  </div>

                  <div className="mt-4 space-y-3">
                    {dashboard?.avisos?.length ? (
                      dashboard.avisos.map(aviso => (
                        <div
                          key={aviso.id}
                          className="rounded-xl border border-[#27272A] p-4"
                        >
                          <div className="flex items-start gap-2">
                            {aviso.fixado && (
                              <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20">
                                Fixado
                              </Badge>
                            )}
                          </div>

                          <p className="font-semibold text-white mt-2">
                            {aviso.titulo}
                          </p>

                          <p className="text-sm text-zinc-500 mt-1 line-clamp-3 whitespace-pre-line">
                            {aviso.mensagem}
                          </p>
                        </div>
                      ))
                    ) : (
                      <EmptyState texto="Nenhum aviso recente." />
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}


          {aba === 'treinos' && (
            <Card className="bg-[#111113] border-[#27272A]">
              <CardContent className="p-5">
                <h2 className="font-bold text-white text-lg">
                  Treinos
                </h2>

                <div className="mt-4 space-y-3">
                  {treinos.length ? (
                    treinos.map(treino => (
                      <div
                        key={treino.id}
                        className="rounded-xl border border-[#27272A] p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                      >
                        <div>
                          <p className="font-semibold text-white">
                            {treino.nome || treino.turma_nome || 'Treino'}
                          </p>

                          <p className="text-sm text-zinc-500 mt-1">
                            {formatarData(treino.data)}
                            {' · '}
                            {treino.horario || '-'}
                            {treino.horario_fim
                              ? ` às ${treino.horario_fim}`
                              : ''}
                          </p>

                          {treino.turma_nome && (
                            <p className="text-xs text-zinc-600 mt-1">
                              {treino.turma_nome}
                            </p>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <EmptyState texto="Nenhum treino encontrado." />
                  )}
                </div>
              </CardContent>
            </Card>
          )}


          {aba === 'presencas' && (
            <Card className="bg-[#111113] border-[#27272A]">
              <CardContent className="p-5">
                <h2 className="font-bold text-white text-lg">
                  Presenças
                </h2>

                <div className="mt-4 space-y-3">
                  {presencas.length ? (
                    presencas.map((presenca, index) => (
                      <div
                        key={presenca.id || index}
                        className="rounded-xl border border-[#27272A] p-4 flex items-center justify-between gap-4"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={[
                              'w-9 h-9 rounded-full flex items-center justify-center',
                              presenca.presente
                                ? 'bg-emerald-500/10'
                                : 'bg-red-500/10'
                            ].join(' ')}
                          >
                            {presenca.presente ? (
                              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                            ) : (
                              <XCircle className="w-5 h-5 text-red-400" />
                            )}
                          </div>

                          <div>
                            <p className="font-medium text-white">
                              {presenca.treino?.nome || 'Treino'}
                            </p>

                            <p className="text-xs text-zinc-500">
                              {formatarData(
                                presenca.treino?.data ||
                                presenca.data
                              )}
                            </p>
                          </div>
                        </div>

                        <Badge
                          className={
                            presenca.presente
                              ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20'
                              : 'bg-red-500/15 text-red-400 border-red-500/20'
                          }
                        >
                          {presenca.presente
                            ? 'Presente'
                            : 'Falta'}
                        </Badge>
                      </div>
                    ))
                  ) : (
                    <EmptyState texto="Nenhuma presença registrada." />
                  )}
                </div>
              </CardContent>
            </Card>
          )}


          {aba === 'mensalidades' && (
            <Card className="bg-[#111113] border-[#27272A]">
              <CardContent className="p-5">
                <h2 className="font-bold text-white text-lg">
                  Mensalidades
                </h2>

                <div className="mt-4 space-y-3">
                  {mensalidades.length ? (
                    mensalidades.map((mensalidade, index) => (
                      <div
                        key={mensalidade.id || index}
                        className={
                          mensalidade.status === 'atrasado'
                            ? 'rounded-xl border border-red-500/30 bg-red-500/[0.05] p-4'
                            : 'rounded-xl border border-[#27272A] p-4'
                        }
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div>
                            <p className="text-xs text-zinc-500">
                              Competência
                            </p>

                            <p className="font-semibold text-white">
                              {formatarCompetencia(
                                mensalidade.competencia
                              )}
                            </p>
                          </div>

                          <div className="sm:text-right">
                            <p className="text-lg font-bold text-white">
                              {formatarMoeda(
                                mensalidade.valor
                              )}
                            </p>

                            <div className="mt-1">
                              {badgeStatusMensalidade(
                                mensalidade.status
                              )}
                            </div>

                            {mensalidade.status === 'atrasado' && (
                              <div className="flex sm:justify-end items-center gap-1.5 mt-2 text-xs font-semibold text-red-400">
                                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                                Pagamento em atraso
                              </div>
                            )}
                          </div>
                        </div>

                        <div
                          className={
                            mensalidade.status === 'atrasado'
                              ? 'border-t border-red-500/20 mt-4 pt-3 text-xs text-red-300/70'
                              : 'border-t border-[#27272A] mt-4 pt-3 text-xs text-zinc-500'
                          }
                        >
                          Vencimento: {' '}
                          {formatarData(
                            mensalidade.data_vencimento
                          )}

                          {mensalidade.data_pagamento && (
                            <>
                              {' · '}
                              Pagamento: {' '}
                              {formatarData(
                                mensalidade.data_pagamento
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <EmptyState texto="Nenhuma mensalidade encontrada." />
                  )}
                </div>
              </CardContent>
            </Card>
          )}


          {aba === 'avisos' && (
            <div className="space-y-3">
              {avisos.length ? (
                avisos.map(aviso => (
                  <Card
                    key={aviso.id}
                    className="bg-[#111113] border-[#27272A]"
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          {aviso.fixado && (
                            <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/20 mb-3">
                              Fixado
                            </Badge>
                          )}

                          <h2 className="font-bold text-white text-lg">
                            {aviso.titulo}
                          </h2>

                          <p className="text-xs text-zinc-600 mt-1">
                            {formatarData(
                              aviso.publicar_em ||
                              aviso.created_at
                            )}
                          </p>
                        </div>

                        <Megaphone className="w-5 h-5 text-zinc-600 shrink-0" />
                      </div>

                      {aviso.imagem_url && (
                        <img
                          src={aviso.imagem_url}
                          alt={aviso.imagem_legenda || aviso.titulo || 'Aviso'}
                          className="w-full max-h-[420px] object-cover rounded-xl mt-4 border border-[#27272A]"
                        />
                      )}

                      <p className="text-sm leading-6 text-zinc-300 mt-4 whitespace-pre-line">
                        {aviso.mensagem}
                      </p>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card className="bg-[#111113] border-[#27272A]">
                  <CardContent>
                    <EmptyState texto="Nenhum aviso disponível." />
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
